/**
 * Member — Stats
 *
 * Three sub-tabs:
 *   1. "Leaderboards" — top run-scorers and top wicket-takers for the selected
 *      season/team.
 *   2. "Recent Matches" — read-only scorecards for recent saved matches, with a
 *      per-match drill-through modal.
 *   3. "All Players" — full table of every player's season/team totals.
 *
 * Fully read-only view for members. All data comes from the shared
 * ``/api/stats/players`` + ``/api/stats/recent-scorecards`` endpoints.
 */
import React, { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import { computePartnerships, bestPartnerships } from "@/lib/partnerships";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  BarChart3,
  Trophy,
  Star,
  CalendarDays,
  ArrowRight,
  Loader2,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { filterClubTeams } from "@/lib/playhq";

const DISMISSAL_LABEL = {
  not_out: "not out",
  bowled: "b",
  caught: "c",
  lbw: "lbw",
  run_out: "run out",
  stumped: "st",
  hit_wicket: "hit wkt",
  retired: "retired",
  obstructing: "obstructing",
  handled_ball: "handled",
  timed_out: "timed out",
};

// ---------------------------------------------------------------------------
// Read-only scorecard viewer
// ---------------------------------------------------------------------------
function InningsView({ innings, title, ourSide }) {
  const batting = innings?.batting || [];
  const bowling = innings?.bowling || [];
  const extras = innings?.extras || {};
  const extraTotal =
    (extras.byes || 0) + (extras.leg_byes || 0) + (extras.wides || 0) +
    (extras.no_balls || 0) + (extras.penalty || 0);

  const dismissalTxt = (b) => {
    const dt = b.dismissal_type;
    if (dt === "not_out") return "not out";
    if (dt === "caught" && b.caught_by_name && b.dismissed_by_name)
      return `c ${b.caught_by_name} b ${b.dismissed_by_name}`;
    if (dt === "run_out" && b.caught_by_name) return `run out (${b.caught_by_name})`;
    if (dt === "stumped" && b.dismissed_by_name && b.caught_by_name)
      return `st ${b.caught_by_name} b ${b.dismissed_by_name}`;
    if (dt === "bowled" && b.dismissed_by_name) return `b ${b.dismissed_by_name}`;
    if (dt === "lbw" && b.dismissed_by_name) return `lbw b ${b.dismissed_by_name}`;
    return DISMISSAL_LABEL[dt] || dt || "—";
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-base font-semibold">
          {title} <Badge variant="secondary" className="ml-2 font-mono text-xs">
            {innings?.total_runs ?? 0}/{innings?.total_wickets ?? 0} · {Number(innings?.overs ?? 0).toFixed(1)} ov
          </Badge>
        </h3>
        {extraTotal > 0 && (
          <div className="text-xs text-muted-foreground">
            Extras {extraTotal} ({extras.byes || 0}b · {extras.leg_byes || 0}lb · {extras.wides || 0}w · {extras.no_balls || 0}nb{extras.penalty ? ` · ${extras.penalty}p` : ""})
          </div>
        )}
      </div>

      {batting.length > 0 && (
        <Card className="rounded-xl overflow-hidden" data-testid="scorecard-view-batting">
          <div className="px-4 py-2.5 border-b border-border text-xs uppercase tracking-wider text-muted-foreground bg-[hsl(var(--secondary))]/50">
            Batting {ourSide ? "(Derwent CC)" : "(Opposition)"}
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[160px]">Batter</TableHead>
                  <TableHead>Dismissal</TableHead>
                  <TableHead className="text-right w-14">R</TableHead>
                  <TableHead className="text-right w-14">B</TableHead>
                  <TableHead className="text-right w-12">4s</TableHead>
                  <TableHead className="text-right w-12">6s</TableHead>
                  <TableHead className="text-right w-14">SR</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {batting.map((b, i) => (
                  <TableRow key={b.id || i}>
                    <TableCell className="font-medium">{b.player_name || "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{dismissalTxt(b)}</TableCell>
                    <TableCell className="text-right font-semibold">{b.runs || 0}</TableCell>
                    <TableCell className="text-right">{b.balls || 0}</TableCell>
                    <TableCell className="text-right">{b.fours || 0}</TableCell>
                    <TableCell className="text-right">{b.sixes || 0}</TableCell>
                    <TableCell className="text-right">
                      {b.balls > 0 ? ((b.runs * 100) / b.balls).toFixed(1) : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {bowling.length > 0 && (
        <Card className="rounded-xl overflow-hidden" data-testid="scorecard-view-bowling">
          <div className="px-4 py-2.5 border-b border-border text-xs uppercase tracking-wider text-muted-foreground bg-[hsl(var(--secondary))]/50">
            Bowling {ourSide ? "(Opposition)" : "(Derwent CC)"}
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[160px]">Bowler</TableHead>
                  <TableHead className="text-right w-14">O</TableHead>
                  <TableHead className="text-right w-12">M</TableHead>
                  <TableHead className="text-right w-14">R</TableHead>
                  <TableHead className="text-right w-14">W</TableHead>
                  <TableHead className="text-right w-14">Econ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bowling.map((b, i) => (
                  <TableRow key={b.id || i}>
                    <TableCell className="font-medium">{b.player_name || "—"}</TableCell>
                    <TableCell className="text-right">{Number(b.overs || 0).toFixed(1)}</TableCell>
                    <TableCell className="text-right">{b.maidens || 0}</TableCell>
                    <TableCell className="text-right">{b.runs || 0}</TableCell>
                    <TableCell className="text-right font-semibold">{b.wickets || 0}</TableCell>
                    <TableCell className="text-right">
                      {b.overs > 0 ? (b.runs / b.overs).toFixed(2) : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <PartnershipsViewMember innings={innings} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Partnerships block (member-facing, read-only). Renders per-wicket
// partnerships + a Best Partnerships list. Hidden entirely if the innings
// has no batting data.
// ---------------------------------------------------------------------------
function PartnershipsViewMember({ innings }) {
  const partnerships = React.useMemo(() => computePartnerships(innings), [innings, computePartnerships]);
  const top5 = React.useMemo(() => bestPartnerships(partnerships, 5), [partnerships, bestPartnerships]);
  if (!partnerships || partnerships.length === 0) return null;
  return (
    <Card className="rounded-xl overflow-hidden" data-testid="scorecard-view-partnerships">
      <div className="px-4 py-2.5 border-b border-border text-xs uppercase tracking-wider text-muted-foreground bg-[hsl(var(--secondary))]/50">
        Partnerships
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">Wkt</TableHead>
              <TableHead>Batters</TableHead>
              <TableHead className="text-right w-28">Partnership</TableHead>
              <TableHead className="text-right w-32">Score at Fall</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {partnerships.map((p) => (
              <TableRow key={`${p.wicket_number}-${p.batters.map((b) => b.order).join("-")}`}>
                <TableCell className="font-medium">{p.wicket_number}</TableCell>
                <TableCell>
                  {p.batters.map((b) => b.name).join(" – ")}
                  {p.unfinished && (
                    <Badge variant="outline" className="ml-2 text-[10px] uppercase tracking-wider">unfinished</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right font-semibold tabular-nums">{p.runs}</TableCell>
                <TableCell className="text-right text-muted-foreground tabular-nums">{p.score_at_fall}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {top5.length > 1 && (
        <div className="px-4 py-3 border-t border-border bg-[hsl(var(--secondary))]/30">
          <div className="text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground mb-2">
            Best partnerships
          </div>
          <ol className="space-y-1">
            {top5.map((p, i) => (
              <li key={`${p.batters.map(b => b.name).join("-")}-${p.runs}-${i}`} className="text-sm flex items-baseline gap-2">
                <span className="text-muted-foreground w-4">{i + 1}.</span>
                <span className="font-medium">{p.batters.map((b) => b.name).join(" & ")}:</span>
                <span className="tabular-nums">{p.runs} runs</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </Card>
  );
}

function ScorecardViewer({ open, item, onClose }) {
  const fx = item?.fixture;
  const sc = item?.scorecard;
  const result = sc?.result || {};
  const outcomeCls =
    result.outcome === "win" ? "bg-emerald-600 text-white" :
    result.outcome === "loss" ? "bg-red-600 text-white" :
    "bg-muted text-foreground";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="member-scorecard-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            Derwent CC vs {fx?.opponent || "Opponent"}
          </DialogTitle>
          <DialogDescription>
            {fx?.date} · {fx?.time} · Round {fx?.round}
            {fx?.team_name_snapshot ? ` · ${fx.team_name_snapshot}` : ""}
          </DialogDescription>
        </DialogHeader>

        {/* Result banner */}
        <Card className="p-4 rounded-2xl flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Badge className={outcomeCls} data-testid="member-scorecard-outcome">
              {result.outcome ? result.outcome.toUpperCase() : "RESULT PENDING"}
            </Badge>
            {result.margin && result.margin_type && (
              <div className="text-sm font-medium">
                by {result.margin} {result.margin_type}
              </div>
            )}
          </div>
          <div className="font-mono text-sm">
            DCC {sc?.our_innings?.total_runs || 0}/{sc?.our_innings?.total_wickets || 0}
            <span className="mx-2 text-muted-foreground">—</span>
            {fx?.opponent || "Opp"} {sc?.opponent_innings?.total_runs || 0}/{sc?.opponent_innings?.total_wickets || 0}
          </div>
        </Card>

        {sc && (
          <div className="mt-2 space-y-6">
            <InningsView innings={sc.our_innings} title="Derwent CC Innings" ourSide={true} />
            <InningsView innings={sc.opponent_innings} title={`${fx?.opponent || "Opposition"} Innings`} ourSide={false} />
            {result.notes && (
              <Card className="p-4 rounded-2xl">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Match Notes</div>
                <div className="text-sm whitespace-pre-line">{result.notes}</div>
              </Card>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Leaderboard column
// ---------------------------------------------------------------------------
function LeaderboardCard({ title, subtitle, icon: Icon, items, primaryKey, primaryLabel, meta, testid }) {
  const { isAdmin } = useAuth();
  return (
    <Card className="rounded-2xl overflow-hidden" data-testid={testid}>
      <div className="p-5 border-b border-border flex items-center gap-3">
        <div className="h-9 w-9 rounded-full bg-[hsl(var(--accent))]/15 flex items-center justify-center">
          <Icon className="h-4 w-4 text-[hsl(var(--accent))]" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate">{title}</div>
          <div className="text-xs text-muted-foreground">{subtitle}</div>
        </div>
      </div>
      {items.length === 0 ? (
        <div className="p-10 text-center text-sm text-muted-foreground">No data yet.</div>
      ) : (
        <ul>
          {items.map((r, i) => (
            <li
              key={(r.player_id || r.player_name) + i}
              className="flex items-center gap-4 px-5 py-3 border-b border-border/60 last:border-b-0"
              data-testid={`${testid}-row-${i}`}
            >
              <span className="w-6 text-center text-xs font-mono text-muted-foreground">{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{r.player_name}</div>
                {isAdmin && r.playhq_id && (
                  <div className="text-[10px] font-mono text-[hsl(var(--accent))]/80 truncate" data-testid={`${testid}-playhq-${i}`} title="PlayHQ player id (admin-only)">
                    PlayHQ · {r.playhq_id}
                  </div>
                )}
                <div className="text-[11px] text-muted-foreground">{meta(r)}</div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold tabular-nums">{r[primaryKey]}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{primaryLabel}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function MemberStats() {
  const [seasons, setSeasons] = useState([]);           // internal seasons from /seasons
  const [activeSeason, setActiveSeason] = useState("__all__");
  const [teams, setTeams] = useState([]);               // PlayHQ teams for active season
  const [activeTeamName, setActiveTeamName] = useState("__all__");
  const [orgId, setOrgId] = useState("");
  const [players, setPlayers] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewerItem, setViewerItem] = useState(null);

  // Load PlayHQ seasons — same list that appears on the Results page.
  useEffect(() => {
    (async () => {
      try {
        const [clubRes, seasonsRes] = await Promise.all([
          api.get("/playhq/club").catch(() => ({ data: {} })),
          api.get("/playhq/seasons"),
        ]);
        setOrgId(clubRes?.data?.org_id || "");
        setSeasons(seasonsRes.data || []);
      } catch (e) {
        toast.error("Failed to load seasons");
      }
    })();
  }, []); // mount-only; api/toast are stable module-level imports

  // Load PlayHQ teams that played in the selected season, filtered to
  // Derwent teams so the dropdown mirrors what appears on Results.
  useEffect(() => {
    (async () => {
      try {
        if (activeSeason === "__all__" || !activeSeason) {
          setTeams([]);
          setActiveTeamName("__all__");
          return;
        }
        const seasonRow = seasons.find((s) => s.name === activeSeason);
        if (!seasonRow?.playhq_id) { setTeams([]); return; }
        const { data } = await api.get(`/playhq/seasons/${seasonRow.playhq_id}/teams`);
        setTeams(filterClubTeams(data || [], orgId));
      } catch (e) {
        setTeams([]);
      }
    })();
  }, [activeSeason, seasons, orgId]); // filterClubTeams is a stable module import

  // Load stats + recent scorecards for the current season + team filter.
  // Leaderboards now come from `/api/playhq/leaderboards` — the legacy
  // `/api/stats/players` endpoint only read from hand-entered
  // `match_scorecards`, which is empty since the PlayCricket data purge.
  // The new endpoint aggregates directly from synced PlayHQ summaries so
  // the boards mirror what members see on Results.
  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const params = new URLSearchParams();
        if (activeSeason && activeSeason !== "__all__") params.set("season", activeSeason);
        if (activeTeamName && activeTeamName !== "__all__") params.set("team", activeTeamName);
        const qs = params.toString();
        const [pRes, rRes] = await Promise.all([
          api.get(`/playhq/leaderboards${qs ? `?${qs}` : ""}`),
          api.get(`/stats/recent-scorecards?${qs ? qs + "&" : ""}limit=20`),
        ]);
        setPlayers(pRes.data?.players || []);
        setRecent(rRes.data?.items || []);
      } catch (e) {
        setPlayers([]);
        setRecent([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [activeSeason, activeTeamName]); // api is a stable module import

  const teamOptions = useMemo(
    () => [{ name: "__all__", label: "All teams" }, ...teams.map((t) => ({
      name: t.name,
      label: t.name,
    }))],
    [teams]
  );
  const activeTeamLabel = teamOptions.find((t) => t.name === activeTeamName)?.label || "All teams";

  const [boardDiscipline, setBoardDiscipline] = useState("batting"); // batting | bowling | fielding
  const [battingSort, setBattingSort] = useState("runs"); // runs|average|highest|centuries|fifties|ducks|not_outs
  const [fieldingSort, setFieldingSort] = useState("total"); // total|catches|wk_catches|non_wk_catches|stumpings|run_outs

  const topBatters = useMemo(() => {
    const rows = [...players].filter((p) => p.innings_batted > 0);
    const sorters = {
      runs:       (a, b) => (b.runs || 0) - (a.runs || 0) || (b.batting_average || 0) - (a.batting_average || 0),
      average:    (a, b) => (b.batting_average ?? -1) - (a.batting_average ?? -1) || (b.runs || 0) - (a.runs || 0),
      highest:    (a, b) => (b.highest || 0) - (a.highest || 0) || (b.runs || 0) - (a.runs || 0),
      centuries:  (a, b) => (b.centuries || 0) - (a.centuries || 0) || (b.runs || 0) - (a.runs || 0),
      fifties:    (a, b) => (b.fifties || 0) - (a.fifties || 0) || (b.runs || 0) - (a.runs || 0),
      ducks:      (a, b) => (b.ducks || 0) - (a.ducks || 0) || (a.runs || 0) - (b.runs || 0),
      not_outs:   (a, b) => (b.not_outs || 0) - (a.not_outs || 0) || (b.runs || 0) - (a.runs || 0),
    };
    const sorted = rows.sort(sorters[battingSort] || sorters.runs);
    // Show only the top 5 batters for the currently-selected metric so
    // the leaderboard stays scannable — matches how the bowling and
    // fielding boards below already limit themselves.
    return sorted.slice(0, 5);
  }, [players, battingSort]);

  // Config for the batting sort-metric buttons + the LeaderboardCard props.
  const battingMetricConfig = useMemo(() => ({
    runs:      { label: "Runs",           key: "runs",           unit: "runs" },
    average:   { label: "Average",        key: "batting_average", unit: "avg"  },
    highest:   { label: "Highest Score",  key: "highest",        unit: "hs"   },
    centuries: { label: "100s",           key: "centuries",      unit: "100s" },
    fifties:   { label: "50s",            key: "fifties",        unit: "50s"  },
    ducks:     { label: "Ducks",          key: "ducks",          unit: "ducks" },
    not_outs:  { label: "Not Outs",       key: "not_outs",       unit: "no"   },
  }), []);
  const topBowlers = useMemo(
    () =>
      [...players]
        .filter((p) => parseFloat(p.overs || "0") > 0 || (p.wickets || 0) > 0)
        .sort(
          (a, b) =>
            (b.wickets || 0) - (a.wickets || 0) ||
            (a.bowling_average ?? 9999) - (b.bowling_average ?? 9999)
        )
        .slice(0, 5),
    [players]
  );
  const topFielders = useMemo(
    () =>
      [...players]
        .map((p) => ({
          ...p,
          _fielding_total: (p.catches || 0) + (p.stumpings || 0) + (p.run_outs || 0),
        }))
        .filter((p) => {
          // Only include players with at least one dismissal for the
          // currently-selected fielding metric so the board doesn't
          // fill with zero-value rows.
          if (fieldingSort === "total") return p._fielding_total > 0;
          if (fieldingSort === "catches") return (p.catches || 0) > 0;
          if (fieldingSort === "wk_catches") return (p.wk_catches || 0) > 0;
          if (fieldingSort === "non_wk_catches") return (p.non_wk_catches || 0) > 0;
          if (fieldingSort === "stumpings") return (p.stumpings || 0) > 0;
          if (fieldingSort === "run_outs") return (p.run_outs || 0) > 0;
          return p._fielding_total > 0;
        })
        .sort((a, b) => {
          const sorters = {
            total:          () => b._fielding_total - a._fielding_total || (b.catches || 0) - (a.catches || 0),
            catches:        () => (b.catches || 0) - (a.catches || 0) || b._fielding_total - a._fielding_total,
            wk_catches:     () => (b.wk_catches || 0) - (a.wk_catches || 0) || (b.catches || 0) - (a.catches || 0),
            non_wk_catches: () => (b.non_wk_catches || 0) - (a.non_wk_catches || 0) || (b.catches || 0) - (a.catches || 0),
            stumpings:      () => (b.stumpings || 0) - (a.stumpings || 0) || b._fielding_total - a._fielding_total,
            run_outs:       () => (b.run_outs || 0) - (a.run_outs || 0) || b._fielding_total - a._fielding_total,
          };
          return (sorters[fieldingSort] || sorters.total)();
        })
        .slice(0, 5),
    [players, fieldingSort]
  );

  // Config for the fielding sort-metric buttons + the LeaderboardCard props,
  // mirroring the batting metric setup above.
  const fieldingMetricConfig = useMemo(() => ({
    total:          { label: "Dismissals",     key: "_fielding_total", unit: "dis"  },
    catches:        { label: "Catches",        key: "catches",         unit: "c"    },
    wk_catches:     { label: "Wk Catches",     key: "wk_catches",      unit: "wkc"  },
    non_wk_catches: { label: "Non-Wk Catches", key: "non_wk_catches",  unit: "nwkc" },
    stumpings:      { label: "Stumpings",      key: "stumpings",       unit: "st"   },
    run_outs:       { label: "Run Outs",       key: "run_outs",        unit: "ro"   },
  }), []);

  return (
    <AppShell variant="member" title="Stats">
      {/* Filters */}
      <Card className="p-5 rounded-2xl mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-medium" htmlFor="member-stats-season-select">Season</Label>
            <Select value={activeSeason || undefined} onValueChange={(v) => setActiveSeason(v)}>
              <SelectTrigger id="member-stats-season-select" data-testid="member-stats-season-select" className="h-11 rounded-lg mt-1.5">
                <SelectValue placeholder={seasons.length ? "Select a season" : "No seasons yet"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__" data-testid="member-stats-season-option-all">All seasons</SelectItem>
                {seasons.map((s) => (
                  <SelectItem key={s.playhq_id || s.id || s.name} value={s.name} data-testid={`member-stats-season-option-${s.playhq_id || s.id || s.name}`}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm font-medium" htmlFor="member-stats-team-select">Team</Label>
            <Select value={activeTeamName} onValueChange={(v) => setActiveTeamName(v)} disabled={activeSeason === "__all__"}>
              <SelectTrigger id="member-stats-team-select" data-testid="member-stats-team-select" className="h-11 rounded-lg mt-1.5">
                <SelectValue>{activeSeason === "__all__" ? "Pick a season first" : activeTeamLabel}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {teamOptions.map((o) => (
                  <SelectItem key={o.name} value={o.name} data-testid={`member-stats-team-option-${o.name}`}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Tabs defaultValue="board" className="mt-2">
        <TabsList data-testid="member-stats-tabs">
          <TabsTrigger value="board" data-testid="member-stats-tab-board">
            <Trophy className="h-4 w-4 mr-1.5" /> Leaderboards
          </TabsTrigger>
          <TabsTrigger value="all" data-testid="member-stats-tab-all">
            <BarChart3 className="h-4 w-4 mr-1.5" /> All Players
          </TabsTrigger>
        </TabsList>

        {/* Leaderboards */}
        <TabsContent value="board" className="pt-4">
          {/* Discipline toggle: Batting / Bowling / Fielding */}
          <div className="mb-4 inline-flex items-center gap-1 rounded-full bg-[hsl(var(--secondary))] p-1" role="tablist" data-testid="member-board-discipline">
            {[
              { key: "batting",  label: "Batting",  icon: Trophy },
              { key: "bowling",  label: "Bowling",  icon: Star },
              { key: "fielding", label: "Fielding", icon: Users },
            ].map((d) => (
              <Button
                key={d.key}
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setBoardDiscipline(d.key)}
                className={`rounded-full px-4 h-9 text-sm ${boardDiscipline === d.key ? "bg-white shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                data-testid={`member-board-tab-${d.key}`}
              >
                <d.icon className="h-4 w-4 mr-1.5" /> {d.label}
              </Button>
            ))}
          </div>

          {/* Batting sort-metric buttons — always visible when Batting is
              active so admins can preview the metric options even if the
              current season/team filter has no data. */}
          {boardDiscipline === "batting" && (
            <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="member-batting-metric-tabs">
              {Object.entries(battingMetricConfig).map(([key, cfg]) => (
                <Button
                  key={key}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setBattingSort(key)}
                  className={`rounded-full h-8 px-3 text-xs ${battingSort === key ? "bg-[hsl(var(--accent))] text-white border-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90" : "hover:border-[hsl(var(--accent))]/40"}`}
                  data-testid={`member-batting-metric-${key}`}
                >
                  {cfg.label}
                </Button>
              ))}
            </div>
          )}

          {/* Fielding sort-metric buttons — mirrors the batting pattern
              so members can drill into Catches / Wk Catches / Stumpings /
              Run Outs individually rather than the combined total. */}
          {boardDiscipline === "fielding" && (
            <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="member-fielding-metric-tabs">
              {Object.entries(fieldingMetricConfig).map(([key, cfg]) => (
                <Button
                  key={key}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setFieldingSort(key)}
                  className={`rounded-full h-8 px-3 text-xs ${fieldingSort === key ? "bg-[hsl(var(--accent))] text-white border-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90" : "hover:border-[hsl(var(--accent))]/40"}`}
                  data-testid={`member-fielding-metric-${key}`}
                >
                  {cfg.label}
                </Button>
              ))}
            </div>
          )}

          {loading ? (
            <Card className="p-14 text-center text-muted-foreground border-dashed rounded-2xl">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" /> Loading leaderboards…
            </Card>
          ) : players.length === 0 ? null : (
            <div className="grid gap-6">
              {boardDiscipline === "batting" && (
                <LeaderboardCard
                  title={`Top ${battingMetricConfig[battingSort].label}`}
                  subtitle={`Ranked by ${battingMetricConfig[battingSort].label.toLowerCase()}`}
                  icon={Trophy}
                  items={topBatters}
                  primaryKey={battingMetricConfig[battingSort].key}
                  primaryLabel={battingMetricConfig[battingSort].unit}
                  meta={(r) => `${r.innings_batted} inn · HS ${r.highest} · Avg ${r.batting_average ?? "—"} · 100s ${r.centuries || 0} · 50s ${r.fifties || 0} · NO ${r.not_outs || 0} · Ducks ${r.ducks || 0}`}
                  testid="member-top-batters"
                />
              )}
              {boardDiscipline === "bowling" && (
                <LeaderboardCard
                  title="Top Wicket-Takers"
                  subtitle="Ranked by wickets"
                  icon={Star}
                  items={topBowlers}
                  primaryKey="wickets"
                  primaryLabel="wkts"
                  meta={(r) => `${Number(r.overs || 0).toFixed(1)} ov · ${r.maidens || 0} mdn · ${r.runs_conceded || 0} r · Avg ${r.bowling_average ?? "—"} · Econ ${r.economy ?? "—"} · BB ${r.best_bowling ?? "—"}`}
                  testid="member-top-bowlers"
                />
              )}
              {boardDiscipline === "fielding" && (
                <LeaderboardCard
                  title={`Top ${fieldingMetricConfig[fieldingSort].label}`}
                  subtitle={fieldingSort === "total" ? "Ranked by dismissal contributions" : `Ranked by ${fieldingMetricConfig[fieldingSort].label.toLowerCase()}`}
                  icon={Users}
                  items={topFielders}
                  primaryKey={fieldingMetricConfig[fieldingSort].key}
                  primaryLabel={fieldingMetricConfig[fieldingSort].unit}
                  meta={(r) => `${r.catches || 0} c · ${r.wk_catches || 0} wkc · ${r.non_wk_catches || 0} nwkc · ${r.stumpings || 0} st · ${r.run_outs || 0} ro`}
                  testid="member-top-fielders"
                />
              )}
            </div>
          )}
        </TabsContent>

        {/* All Player Totals */}
        <TabsContent value="all" className="pt-4">
          {players.length === 0 ? null : (
            <Card className="rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="sticky left-0 bg-background z-10 min-w-[180px]">Player</TableHead>
                      <TableHead className="text-right">M</TableHead>
                      <TableHead className="text-right">Inn</TableHead>
                      <TableHead className="text-right">Runs</TableHead>
                      <TableHead className="text-right">HS</TableHead>
                      <TableHead className="text-right">Avg</TableHead>
                      <TableHead className="text-right">4s</TableHead>
                      <TableHead className="text-right">6s</TableHead>
                      <TableHead className="text-right">Ov</TableHead>
                      <TableHead className="text-right">Mdns</TableHead>
                      <TableHead className="text-right">Runs C</TableHead>
                      <TableHead className="text-right">Wkts</TableHead>
                      <TableHead className="text-right">B-Avg</TableHead>
                      <TableHead className="text-right">Econ</TableHead>
                      <TableHead className="text-right">Ct</TableHead>
                      <TableHead className="text-right">WkC</TableHead>
                      <TableHead className="text-right">NWkC</TableHead>
                      <TableHead className="text-right">RO</TableHead>
                      <TableHead className="text-right">St</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {players.map((p, idx) => (
                      <TableRow key={(p.player_id || p.player_name) + idx} data-testid={`member-all-row-${idx}`}>
                        <TableCell className="sticky left-0 bg-background font-medium">{p.player_name}</TableCell>
                        <TableCell className="text-right">{p.matches}</TableCell>
                        <TableCell className="text-right">{p.innings_batted}</TableCell>
                        <TableCell className="text-right font-semibold">{p.runs}</TableCell>
                        <TableCell className="text-right">{p.highest}</TableCell>
                        <TableCell className="text-right">{p.batting_average ?? "—"}</TableCell>
                        <TableCell className="text-right">{p.fours}</TableCell>
                        <TableCell className="text-right">{p.sixes}</TableCell>
                        <TableCell className="text-right">{Number(p.overs || 0).toFixed(1)}</TableCell>
                        <TableCell className="text-right">{p.maidens || 0}</TableCell>
                        <TableCell className="text-right">{p.runs_conceded || 0}</TableCell>
                        <TableCell className="text-right font-semibold">{p.wickets}</TableCell>
                        <TableCell className="text-right">{p.bowling_average ?? "—"}</TableCell>
                        <TableCell className="text-right">{p.economy ?? "—"}</TableCell>
                        <TableCell className="text-right">{p.catches}</TableCell>
                        <TableCell className="text-right">{p.wk_catches || 0}</TableCell>
                        <TableCell className="text-right">{p.non_wk_catches || 0}</TableCell>
                        <TableCell className="text-right">{p.run_outs}</TableCell>
                        <TableCell className="text-right">{p.stumpings}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      <ScorecardViewer
        open={!!viewerItem}
        item={viewerItem}
        onClose={() => setViewerItem(null)}
      />
    </AppShell>
  );
}
